<template>
  <b-card
    class="revenue-trend-card mb-0 h-100"
    body-class="d-flex flex-column h-100"
  >
    <div class="d-flex align-items-center justify-content-between mb-1">
      <div class="d-flex align-items-center">
        <b-avatar
          variant="light-info"
          size="30"
          class="mr-50"
        >
          <b-spinner
            v-if="loading"
            small
            variant="primary"
          />
          <feather-icon
            v-else
            icon="MessageSquareIcon"
            size="15"
          />
        </b-avatar>
        <small class="text-muted">Website Chat Users</small>
      </div>

      <b-button-group size="sm">
        <b-button
          v-for="option in periodOptions"
          :key="option.value"
          :variant="period === option.value ? 'primary' : 'outline-secondary'"
          :disabled="loading"
          @click="changePeriod(option.value)"
        >
          {{ option.label }}
        </b-button>
      </b-button-group>
    </div>

    <div :class="{ 'opacity-50': loading }">
      <h2 class="font-weight-bolder mb-25">
        {{ total.toLocaleString('en-US') }}
      </h2>
      <small class="text-muted">people started a website chat {{ periodDescription }}</small>
    </div>

    <vue-apex-charts
      type="bar"
      height="160"
      class="mt-auto"
      :class="{ 'opacity-50': loading }"
      :options="chartOptions"
      :series="chartSeries"
    />
  </b-card>
</template>

<script>
import {
  BCard, BAvatar, BSpinner, BButtonGroup, BButton,
} from 'bootstrap-vue'
import VueApexCharts from 'vue-apexcharts'
import moment from 'moment'
import chatbotAxios from '@/libs/chatbot-axios'
import { $themeColors } from '@themeConfig'

// Each conversation's user_id is prefixed `web_` when it originates from the
// website chat widget, which is how ai-chat-bot's /conversations/web/stats
// endpoint tells it apart from LINE conversations server-side.
const PERIOD_CONFIG = {
  week: { label: 'Week', days: 7, description: 'this week' },
  month: { label: 'Month', days: 30, description: 'this month' },
  year: { label: 'Year', days: 365, description: 'this year' },
}

export default {
  components: {
    BCard,
    BAvatar,
    BSpinner,
    BButtonGroup,
    BButton,
    VueApexCharts,
  },
  data() {
    return {
      loading: true,
      period: 'month',
      total: 0,
      history: [],
    }
  },
  computed: {
    periodOptions() {
      return Object.entries(PERIOD_CONFIG).map(([value, config]) => ({ value, label: config.label }))
    },
    periodDescription() {
      return PERIOD_CONFIG[this.period].description
    },
    chartSeries() {
      return [{ name: 'Conversations', data: this.history.map(point => point.value) }]
    },
    chartOptions() {
      return {
        chart: {
          type: 'bar',
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        plotOptions: {
          bar: { columnWidth: this.period === 'year' ? '50%' : '35%', borderRadius: 3 },
        },
        grid: {
          borderColor: '#EBEBEB',
          strokeDashArray: 5,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: false } },
          padding: {
            top: 24, right: 30, bottom: 0, left: 30,
          },
        },
        dataLabels: { enabled: false },
        colors: [$themeColors.info],
        xaxis: {
          categories: this.history.map(point => point.label),
          labels: {
            show: true,
            style: { fontSize: '11px' },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: { show: false },
        tooltip: {
          x: { show: true },
          y: { formatter: value => `${value} conversation${value === 1 ? '' : 's'}` },
        },
      }
    },
  },
  created() {
    this.fetchStats()
  },
  methods: {
    changePeriod(period) {
      if (period === this.period) return
      this.period = period
      this.fetchStats()
    },
    fetchStats() {
      this.loading = true
      const { days } = PERIOD_CONFIG[this.period]

      return chatbotAxios.get('/api/v1/conversations/web/stats/daily', { params: { days } })
        .then(({ data }) => {
          const series = data.data || data
          this.history = this.period === 'year' ? this.bucketByMonth(series) : this.bucketByDay(series)
          this.total = series.reduce((sum, point) => sum + point.count, 0)
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    bucketByDay(series) {
      const labelFormat = this.period === 'week' ? 'ddd D' : 'MMM D'
      return series.map(point => ({ label: moment(point.date).format(labelFormat), value: point.count }))
    },
    bucketByMonth(series) {
      const months = new Map()
      series.forEach(point => {
        const key = moment(point.date).format('YYYY-MM')
        months.set(key, (months.get(key) || 0) + point.count)
      })
      return Array.from(months.entries()).map(([key, value]) => ({ label: moment(key, 'YYYY-MM').format('MMM'), value }))
    },
  },
}
</script>

<style scoped>
.opacity-50 {
  opacity: 0.5;
  transition: opacity 0.15s ease-in-out;
}
</style>
